<template>
  <AsyncModal
    :fetch="fetchSellerDetails"
    :name="MODAL_NAMES.SELLER_DETAILS"
    :title="i18n(translations.title)"
  >
    <template #content v-if="sellerDetails">
      <section class="pb-32">
        <h2 class="heading-2">
          {{ i18n(translations.section1Title) }}
        </h2>
        <div v-if="sellerDetails.manufacturer">
          <div class="pt-16">
            <p class="body-1">
              {{ sellerDetails.manufacturer.companyName }}
            </p>
          </div>
          <div class="pt-8">
            <p class="caption-bold">
              {{ i18n(translations.section1Item2) }}
            </p>
            <p class="body-1">
              {{ sellerDetails.manufacturer.brand }}
            </p>
          </div>
          <div class="pt-8">
            <p class="caption-bold">
              {{ i18n(translations.section1Item3) }}
            </p>
            <p class="body-1">
              {{ sellerDetails.manufacturer.email }}
            </p>
          </div>
          <div class="pt-8">
            <p class="caption-bold">
              {{ i18n(translations.section1Item4) }}
            </p>
            <p class="body-1">
              {{ sellerDetails.manufacturer.address }}
            </p>
          </div>
        </div>
        <SellerDetailsModalNoInfo v-else class="mt-16" />
      </section>
      <RevDivider class="pb-32" />
      <section class="pb-32">
        <h2 class="heading-2">{{ i18n(translations.section2Title) }}</h2>
        <div v-if="sellerDetails.responsibleOperator">
          <div class="pt-16">
            <p class="body-1">
              {{ sellerDetails.responsibleOperator.businessName }}
            </p>
          </div>
          <div class="pt-8">
            <p class="caption-bold">
              {{ i18n(translations.section2Item2) }}
            </p>
          </div>
          <div class="pt-8">
            <p class="body-1">
              {{ sellerDetails.responsibleOperator.email }}
            </p>
          </div>
          <div class="pt-8">
            <p class="body-1">
              {{ sellerDetails.responsibleOperator.phone }}
            </p>
          </div>
          <div class="pt-8">
            <p class="body-1">
              {{ sellerDetails.responsibleOperator.website }}
            </p>
          </div>
          <div class="pt-8">
            <p class="caption-bold">
              {{ i18n(translations.section2Item3) }}
            </p>
            <p class="body-1">
              {{ sellerDetails.responsibleOperator.addressLine1 }}
            </p>
          </div>
        </div>
        <SellerDetailsModalNoInfo v-else class="mt-16" />
      </section>
      <RevDivider class="pb-32" />
      <section>
        <h2 class="heading-2">{{ i18n(translations.section3Title) }}</h2>
        <div class="pt-16">
          <div class="flex">
            <RevCountryFlag country-code="EU" />
            <span class="body-1-bold pl-8">
              {{ i18n(translations.section3Item1Title) }}
            </span>
          </div>
          <div class="body-2">
            {{ i18n(translations.section3Item1Text) }}
          </div>
        </div>
        <div v-if="lifespan" class="pt-20">
          <div class="body-1-bold">
            {{ i18n(translations.section3Item2Title) }}
          </div>
          <div class="body-1">
            {{
              i18n(translations.section3Item2Date, {
                years: lifespan.years,
                months: lifespan.months,
              })
            }}
          </div>
        </div>
        <RevButton
          v-if="sellerDetails?.merchant?.link"
          class="mt-20"
          full-width="always"
          size="medium"
          :to="sellerDetails.merchant.link"
          variant="secondary"
        >
          {{ i18n(translations.section3Button) }}
        </RevButton>
      </section>
    </template>
  </AsyncModal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import {
  type GetSellerDetails,
  getSellerDetails,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/seller-details'
import AsyncModal from '@backmarket/nuxt-layer-product-modals/AsyncModal.vue'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCountryFlag } from '@ds/components/CountryFlag'
import { RevDivider } from '@ds/components/Divider'

import { MODAL_NAMES } from '~/scopes/product/constants'
import { monthDiff } from '~/scopes/product/utils/monthDiff'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

import translations from './SellerDetailsModal.translations'
import SellerDetailsModalNoInfo from './components/SellerDetailsModalNoInfo/SellerDetailsModalNoInfo.vue'

const props = defineProps<{
  listingId?: string | null
}>()

const i18n = useI18n()
const sellerDetails = ref<GetSellerDetails | undefined>(undefined)
const { productId } = useUrlParams()

const lifespan = computed(() => {
  if (sellerDetails.value?.merchant) {
    const sellerDate = new Date(sellerDetails.value?.merchant?.sellerSinceDate)
    const currentDate = new Date(Date.now())

    const years = Math.trunc(monthDiff(sellerDate, currentDate) / 12)

    const months = monthDiff(sellerDate, currentDate) - years * 12

    return { years, months }
  }

  return {}
})
async function fetchSellerDetails() {
  const { data } = await useHttpFetch(getSellerDetails, {
    pathParams: { id: productId, listingId: String(props.listingId) },
  })
  sellerDetails.value = data.value
}
</script>
